import React, { useState, useRef } from "react";

const API = "https://canada-billing.ameontech.com";

const PROVINCES  = ["AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT"];
const CURRENCIES = ["CAD","USD","EUR","GBP"];
const DATE_FORMATS = [
  { value: "YYYY-MM-DD",   label: "YYYY-MM-DD  (2025-06-06)" },
  { value: "DD/MM/YYYY",   label: "DD/MM/YYYY  (06/06/2025)" },
  { value: "MM/DD/YYYY",   label: "MM/DD/YYYY  (06/06/2025)" },
  { value: "MMM DD, YYYY", label: "MMM DD, YYYY  (Jun 06, 2025)" },
];

const inp = {
  width: "100%", padding: "10px 13px", borderRadius: 9,
  border: "1.5px solid #e8edf3", fontSize: 14, color: "#1a1a2e",
  outline: "none", boxSizing: "border-box", background: "#fafbfd",
  fontFamily: "'DM Sans', sans-serif", transition: "border .2s",
};
const focusInp = (e) => (e.target.style.borderColor = "#4f8ef7");
const blurInp  = (e) => (e.target.style.borderColor = "#e8edf3");

const Field = ({ label, sub, children, required }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
    <label style={{ fontSize: 12.5, fontWeight: 600, color: "#374151", display:"flex", gap:4 }}>
      {label}
      {required && <span style={{ color: "#f97b6b" }}>*</span>}
    </label>
    {sub && <span style={{ fontSize: 11.5, color: "#9ca3af", marginTop: -2 }}>{sub}</span>}
    {children}
  </div>
);

const Row2 = ({ children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
    {children}
  </div>
);

const Divider = ({ label }) => (
  <div style={{ display:"flex", alignItems:"center", gap:10, margin:"8px 0 4px" }}>
    <div style={{ flex:1, height:1, background:"#f0f2f5" }} />
    <span style={{ fontSize:11, fontWeight:700, letterSpacing:"1px", textTransform:"uppercase", color:"#c0c8d4" }}>{label}</span>
    <div style={{ flex:1, height:1, background:"#f0f2f5" }} />
  </div>
);

const STEPS = ["Personal & Business", "Address", "Invoice Setup"];

const StepBar = ({ step }) => (
  <div style={{ display:"flex", alignItems:"center", gap:0, marginBottom: 28 }}>
    {STEPS.map((s, i) => {
      const done    = i < step;
      const current = i === step;
      return (
        <React.Fragment key={s}>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
            <div style={{
              width: 30, height: 30, borderRadius: "50%",
              background: done ? "#4f8ef7" : current ? "linear-gradient(135deg,#4f8ef7,#6366f1)" : "#f0f2f5",
              color: done || current ? "#fff" : "#9ca3af",
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize: 13, fontWeight: 700,
              border: current ? "2px solid #4f8ef7" : "none",
              boxShadow: current ? "0 0 0 4px rgba(79,142,247,.15)" : "none",
              transition: "all .3s",
            }}>
              {done ? "✓" : i + 1}
            </div>
            <span style={{ fontSize:11, fontWeight: current ? 700 : 500, color: current ? "#4f8ef7" : done ? "#4f8ef7" : "#9ca3af", whiteSpace:"nowrap" }}>
              {s}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div style={{ flex:1, height:2, background: done ? "#4f8ef7" : "#f0f2f5", margin:"0 6px", marginBottom:20, transition:"background .3s" }} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

export default function BusinessOnboarding({ onComplete, onSkip }) {
  const [step, setStep]     = useState(0);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const logoRef             = useRef(null);
  const signatureRef        = useRef(null);

  const [form, setForm] = useState({
    // ── Personal Info (NEW) ──
    fullName: "", email: "", phone: "", website: "",
    // ── Business Info ──
    company: "", businessType: "", businessNumber: "", hstNumber: "",
    naicsCode: "",
    // ── Logo & Signature (NEW) ──
    logoUrl: "", signatureUrl: "",
    // ── Address ──
    addressLine1: "", addressLine2: "", city: "", province: "ON",
    postalCode: "", country: "Canada",
    // ── Invoice Settings ──
    currency: "CAD", dateFormat: "MM/DD/YYYY",
    invoicePrefix: "INV", invoiceStartNumber: 1, dueDays: 30,
    footerNote: "", terms: "",
  });

  const set = (key) => (e) => {
    setForm(p => ({ ...p, [key]: e.target.value }));
    setErrors(p => ({ ...p, [key]: "" }));
  };

  // Logo Upload
  const handleLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("logo", file);
    try {
      const token = localStorage.getItem("token");
      const res   = await fetch(`${API}/api/business-profile/logo`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setForm(p => ({ ...p, logoUrl: `${API}${data.logo_url}` }));
    } catch (err) { console.error(err); }
  };

  // Signature Upload (NEW)
  const handleSignature = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("signature", file);
    try {
      const token = localStorage.getItem("token");
      const res   = await fetch(`${API}/api/business-profile/signature`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setForm(p => ({ ...p, signatureUrl: `${API}${data.signature_url}` }));
    } catch (err) { console.error(err); }
  };

  const validate = () => {
    const e = {};
    if (step === 0 && !form.fullName.trim()) e.fullName = "Full name is required";
    if (step === 0 && !form.company.trim())  e.company  = "Business name is required";
    if (step === 1 && !form.city.trim())     e.city     = "City is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validate()) setStep(s => s + 1); };
  const back = () => setStep(s => s - 1);

  const handleFinish = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/api/business-profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          // Personal Info (NEW)
          full_name: form.fullName,
          email:     form.email,
          phone:     form.phone,
          website:   form.website,
          // Business Info
          business_name:        form.company,
          business_type:        form.businessType,
          business_number:      form.businessNumber,
          hst_number:           form.hstNumber,
          naics_code:           form.naicsCode,
          // Address
          address_line1:        form.addressLine1,
          address_line2:        form.addressLine2,
          city:                 form.city,
          province:             form.province,
          postal_code:          form.postalCode,
          country:              form.country,
          // Invoice Settings
          currency:             form.currency,
          date_format:          form.dateFormat,
          invoice_prefix:       form.invoicePrefix,
          invoice_start_number: Number(form.invoiceStartNumber),
          due_days:             Number(form.dueDays),
          footer_note:          form.footerNote,
          terms:                form.terms,
          auto_round: false, tax_inclusive: false,
        }),
      });
      if (!res.ok) throw new Error("Save failed");
      onComplete?.();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const renderStep = () => {
    // ── STEP 0: Personal + Business Info ──
    if (step === 0) return (
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

        {/* Personal Information */}
        <Divider label="Personal Information" />
        <Row2>
          <Field label="Full Name" required>
            <input style={{ ...inp, borderColor: errors.fullName ? "#f97b6b" : "#e8edf3" }}
              value={form.fullName} onChange={set("fullName")} placeholder="John Doe"
              onFocus={focusInp} onBlur={blurInp} />
            {errors.fullName && <span style={{ fontSize:11.5, color:"#f97b6b" }}>{errors.fullName}</span>}
          </Field>
          <Field label="Email Address">
            <input style={inp} type="email" value={form.email} onChange={set("email")}
              placeholder="john@example.com" onFocus={focusInp} onBlur={blurInp} />
          </Field>
        </Row2>
        <Row2>
          <Field label="Phone Number">
            <input style={inp} value={form.phone} onChange={set("phone")}
              placeholder="416-555-0123" onFocus={focusInp} onBlur={blurInp} />
          </Field>
          <Field label="Website">
            <input style={inp} value={form.website} onChange={set("website")}
              placeholder="https://yoursite.ca" onFocus={focusInp} onBlur={blurInp} />
          </Field>
        </Row2>

        {/* Business Information */}
        <Divider label="Business Information" />

        {/* Logo + Signature Upload */}
        <Row2>
          {/* Logo */}
          <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 14px", background:"rgba(79,142,247,.04)", borderRadius:12, border:"1.5px dashed rgba(79,142,247,.25)" }}>
            <div style={{ width:46, height:46, borderRadius:9, background:"rgba(79,142,247,.1)", border:"1.5px solid rgba(79,142,247,.2)", overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              {form.logoUrl
                ? <img src={form.logoUrl} alt="logo" style={{ width:"100%", height:"100%", objectFit:"contain" }} />
                : <span style={{ fontSize:20 }}>🏢</span>}
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:12.5, fontWeight:600, color:"#374151" }}>Business Logo</div>
              <div style={{ fontSize:11.5, color:"#9ca3af", marginBottom:6 }}>PNG / JPG / SVG</div>
              <button onClick={() => logoRef.current?.click()} style={{ padding:"4px 12px", borderRadius:20, border:"1.5px solid #4f8ef7", background:"transparent", color:"#4f8ef7", fontSize:12, fontWeight:600, cursor:"pointer" }}>
                {form.logoUrl ? "Change" : "Upload"}
              </button>
              <input ref={logoRef} type="file" accept="image/*" style={{ display:"none" }} onChange={handleLogo} />
            </div>
          </div>

          {/* Signature (NEW) */}
          <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 14px", background:"rgba(99,102,241,.04)", borderRadius:12, border:"1.5px dashed rgba(99,102,241,.25)" }}>
            <div style={{ width:46, height:46, borderRadius:9, background:"rgba(99,102,241,.1)", border:"1.5px solid rgba(99,102,241,.2)", overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              {form.signatureUrl
                ? <img src={form.signatureUrl} alt="signature" style={{ width:"100%", height:"100%", objectFit:"contain" }} />
                : <span style={{ fontSize:20 }}>✍️</span>}
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:12.5, fontWeight:600, color:"#374151" }}>Signature</div>
              <div style={{ fontSize:11.5, color:"#9ca3af", marginBottom:6 }}>PNG / JPG</div>
              <button onClick={() => signatureRef.current?.click()} style={{ padding:"4px 12px", borderRadius:20, border:"1.5px solid #6366f1", background:"transparent", color:"#6366f1", fontSize:12, fontWeight:600, cursor:"pointer" }}>
                {form.signatureUrl ? "Change" : "Upload"}
              </button>
              <input ref={signatureRef} type="file" accept="image/*" style={{ display:"none" }} onChange={handleSignature} />
            </div>
          </div>
        </Row2>

        <Row2>
          <Field label="Business / Company Name" required>
            <input style={{ ...inp, borderColor: errors.company ? "#f97b6b" : "#e8edf3" }}
              value={form.company} onChange={set("company")} placeholder="Smith Consulting Inc."
              onFocus={focusInp} onBlur={blurInp} />
            {errors.company && <span style={{ fontSize:11.5, color:"#f97b6b" }}>{errors.company}</span>}
          </Field>
          <Field label="Business Type" sub="Sole Proprietor, Corporation…">
            <input style={inp} value={form.businessType} onChange={set("businessType")} placeholder="Corporation" onFocus={focusInp} onBlur={blurInp} />
          </Field>
        </Row2>
        <Row2>
          <Field label="Business Number (BN)" sub="CRA 9-digit number">
            <input style={inp} value={form.businessNumber} onChange={set("businessNumber")} placeholder="123456789" onFocus={focusInp} onBlur={blurInp} />
          </Field>
          <Field label="GST / HST Number" sub="e.g. 123456789 RT0001">
            <input style={inp} value={form.hstNumber} onChange={set("hstNumber")} placeholder="123456789 RT0001" onFocus={focusInp} onBlur={blurInp} />
          </Field>
        </Row2>
        <Row2>
          <Field label="NAICS Code" sub="6-digit industry classification">
            <input style={inp} value={form.naicsCode} onChange={set("naicsCode")} placeholder="541511" onFocus={focusInp} onBlur={blurInp} />
          </Field>
          <div />
        </Row2>
      </div>
    );

    // ── STEP 1: Address ──
    if (step === 1) return (
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        <Field label="Address Line 1">
          <input style={inp} value={form.addressLine1} onChange={set("addressLine1")} placeholder="123 Main St" onFocus={focusInp} onBlur={blurInp} />
        </Field>
        <Field label="Address Line 2" sub="Suite, Unit, Floor (optional)">
          <input style={inp} value={form.addressLine2} onChange={set("addressLine2")} placeholder="Suite 100" onFocus={focusInp} onBlur={blurInp} />
        </Field>
        <Row2>
          <Field label="City" required>
            <input style={{ ...inp, borderColor: errors.city ? "#f97b6b" : "#e8edf3" }}
              value={form.city} onChange={set("city")} placeholder="Toronto" onFocus={focusInp} onBlur={blurInp} />
            {errors.city && <span style={{ fontSize:11.5, color:"#f97b6b" }}>{errors.city}</span>}
          </Field>
          <Field label="Province">
            <select style={inp} value={form.province} onChange={set("province")}>
              {PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
        </Row2>
        <Row2>
          <Field label="Postal Code">
            <input style={inp} value={form.postalCode} onChange={set("postalCode")} placeholder="M5V 1A1" onFocus={focusInp} onBlur={blurInp} />
          </Field>
          <Field label="Country">
            <input style={inp} value={form.country} onChange={set("country")} placeholder="Canada" onFocus={focusInp} onBlur={blurInp} />
          </Field>
        </Row2>
      </div>
    );

    // ── STEP 2: Invoice Setup ──
    if (step === 2) return (
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        <Row2>
          <Field label="Currency">
            <select style={inp} value={form.currency} onChange={set("currency")}>
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Date Format" sub="How dates appear on invoices">
            <select style={inp} value={form.dateFormat} onChange={set("dateFormat")}>
              {DATE_FORMATS.map(df => <option key={df.value} value={df.value}>{df.label}</option>)}
            </select>
          </Field>
        </Row2>
        <Row2>
          <Field label="Invoice Prefix" sub="e.g. INV, BILL">
            <input style={inp} value={form.invoicePrefix} onChange={set("invoicePrefix")} placeholder="INV" onFocus={focusInp} onBlur={blurInp} />
          </Field>
          <Field label="Invoice Start Number">
            <input style={inp} type="number" value={form.invoiceStartNumber} onChange={set("invoiceStartNumber")} placeholder="1" onFocus={focusInp} onBlur={blurInp} />
          </Field>
        </Row2>
        <Row2>
          <Field label="Default Due Days" sub="Days until payment due">
            <input style={inp} type="number" value={form.dueDays} onChange={set("dueDays")} placeholder="30" onFocus={focusInp} onBlur={blurInp} />
          </Field>
          <div />
        </Row2>
        <Divider label="Invoice Footer" />
        <Field label="Footer Note" sub="Shown at the bottom of every invoice">
          <textarea style={{ ...inp, resize:"vertical" }} rows={2}
            value={form.footerNote} onChange={set("footerNote")}
            placeholder="Thank you for your business!"
            onFocus={focusInp} onBlur={blurInp} />
        </Field>
        <Field label="Terms & Conditions">
          <textarea style={{ ...inp, resize:"vertical" }} rows={3}
            value={form.terms} onChange={set("terms")}
            placeholder="Payment due within 30 days..."
            onFocus={focusInp} onBlur={blurInp} />
        </Field>
      </div>
    );
  };

  const stepMeta = [
    { icon:"👤", title:"Personal & Business Info", sub:"Your details and company information for invoices." },
    { icon:"📍", title:"Where are you located?",   sub:"Used for your invoice sender address." },
    { icon:"🧾", title:"Set up your invoicing",     sub:"Customize how your invoices are numbered and formatted." },
  ];
  const meta = stepMeta[step];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=DM+Sans:wght@300;400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin:0; padding:0; }
        html, body { height:100%; background:#f0f4fa; }
        .ob-wrap { min-height:100vh; display:flex; background:#f0f4fa; font-family:'DM Sans',sans-serif; }
        .ob-left { width:340px; min-height:100vh; background:#13131f; display:flex; flex-direction:column; padding:40px 36px; position:relative; overflow:hidden; flex-shrink:0; }
        .ob-left::before { content:''; position:absolute; width:320px; height:320px; border-radius:50%; background:#4f8ef7; filter:blur(100px); opacity:.09; top:-100px; right:-80px; pointer-events:none; }
        .ob-left::after  { content:''; position:absolute; width:200px; height:200px; border-radius:50%; background:#6366f1; filter:blur(80px); opacity:.07; bottom:60px; left:-60px; pointer-events:none; }
        .ob-right { flex:1; display:flex; align-items:center; justify-content:center; padding:40px 32px; overflow-y:auto; }
        .ob-card  { background:#fff; border-radius:20px; width:100%; max-width:560px; padding:36px 38px 32px; box-shadow:0 4px 32px rgba(0,0,0,.08); }
        .ob-nav-btn { display:inline-flex; align-items:center; gap:6px; padding:10px 22px; border-radius:50px; font-size:14px; font-weight:600; cursor:pointer; transition:all .15s; border:none; font-family:'DM Sans',sans-serif; }
        .ob-btn-primary { background:linear-gradient(135deg,#4f8ef7,#6366f1); color:#fff; }
        .ob-btn-primary:hover { opacity:.9; }
        .ob-btn-primary:disabled { opacity:.5; cursor:not-allowed; }
        .ob-btn-ghost { background:transparent; border:1.5px solid #e8edf3 !important; color:#6b7280; }
        .ob-btn-ghost:hover { border-color:#4f8ef7 !important; color:#4f8ef7; }
        .ob-skip { background:none; border:none; cursor:pointer; font-size:12.5px; color:rgba(255,255,255,.3); font-family:'DM Sans',sans-serif; margin-top:8px; transition:color .15s; }
        .ob-skip:hover { color:rgba(255,255,255,.6); }
        @keyframes fadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        .ob-step-content { animation:fadeUp .25s ease; }
      `}</style>

      <div className="ob-wrap">
        <div className="ob-left">
          <div style={{ display:"flex", alignItems:"center", gap:9, marginBottom:48, position:"relative", zIndex:2 }}>
            <div style={{ width:34, height:34, borderRadius:9, background:"linear-gradient(135deg,#4f8ef7,#6366f1)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>🍁</div>
            <div>
              <div style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:19, color:"#fff", lineHeight:1 }}>Invoice<span style={{ color:"#4f8ef7" }}>Cloud</span></div>
              <div style={{ fontSize:10, fontWeight:700, letterSpacing:"1.4px", color:"#4f8ef7", textTransform:"uppercase", marginTop:2 }}>Canada</div>
            </div>
          </div>
          <div style={{ position:"relative", zIndex:2, flex:1 }}>
            <div style={{ fontSize:11, fontWeight:700, letterSpacing:"1.5px", textTransform:"uppercase", color:"#4f8ef7", marginBottom:10 }}>Welcome aboard 🎉</div>
            <h2 style={{ fontFamily:"'Playfair Display',serif", fontWeight:800, fontSize:26, color:"#fff", lineHeight:1.3, marginBottom:14 }}>
              Let's set up your<br />business profile
            </h2>
            <p style={{ fontSize:14, color:"rgba(255,255,255,.45)", lineHeight:1.7, marginBottom:36 }}>
              Just 3 quick steps and your invoicing workspace will be ready. Takes less than 2 minutes.
            </p>
            {[
              { icon:"👤", label:"Personal & business info" },
              { icon:"📍", label:"Business address" },
              { icon:"🧾", label:"Invoice preferences" },
            ].map((item, i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
                <div style={{ width:32, height:32, borderRadius:8, background: i < step ? "rgba(82,201,126,.15)" : i === step ? "rgba(79,142,247,.15)" : "rgba(255,255,255,.05)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, border: i === step ? "1px solid rgba(79,142,247,.3)" : "none", transition:"all .3s" }}>
                  {i < step ? "✓" : item.icon}
                </div>
                <span style={{ fontSize:13.5, fontWeight: i === step ? 600 : 400, color: i < step ? "rgba(82,201,126,.8)" : i === step ? "#fff" : "rgba(255,255,255,.35)", transition:"color .3s" }}>
                  {item.label}
                </span>
              </div>
            ))}
          </div>
          <div style={{ position:"relative", zIndex:2, marginTop:16 }}>
            <button className="ob-skip" onClick={() => onSkip?.()}>Skip setup for now →</button>
          </div>
        </div>

        <div className="ob-right">
          <div className="ob-card">
            <StepBar step={step} />
            <div className="ob-step-content" key={step}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
                <div style={{ width:38, height:38, borderRadius:10, background:"rgba(79,142,247,.1)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>
                  {meta.icon}
                </div>
                <div>
                  <div style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:17, color:"#1a1a2e" }}>{meta.title}</div>
                  <div style={{ fontSize:12.5, color:"#9ca3af", marginTop:2 }}>{meta.sub}</div>
                </div>
              </div>

              {renderStep()}

              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:26 }}>
                <div>
                  {step > 0 && (
                    <button className="ob-nav-btn ob-btn-ghost" onClick={back}>← Back</button>
                  )}
                </div>
                <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                  <span style={{ fontSize:12, color:"#c0c8d4" }}>Step {step + 1} of {STEPS.length}</span>
                  {step < STEPS.length - 1
                    ? <button className="ob-nav-btn ob-btn-primary" onClick={next}>Continue →</button>
                    : <button className="ob-nav-btn ob-btn-primary" onClick={handleFinish} disabled={saving}>
                        {saving ? "Saving…" : "Finish Setup ✓"}
                      </button>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}